// Cola de informes — encender/apagar, separación entre informes, cola por estudiante y quitar estudiantes.
// Solo administrador. Se actualiza sola cada 5 s.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Power, Loader2, Timer, Trash2, UserX, X, ListChecks, Minus, Plus, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const TYPE_LABEL = {
  ruta_ensenanza: "Ruta de enseñanza", cep: "CEP", cad: "CAD", integral: "Integral",
  ice: "ICE", andamiaje: "Andamiaje", plan_individual: "Plan individual",
};
const PRESETS = [0, 1, 2, 5, 10];
const BASE = "/diagnostics/reports/queue";

const fmtClock = (secs) => {
  const s = Math.max(0, Math.round(secs));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};
const fmtGap = (g) => (g === 0 ? "Sin espera (uno a la vez)" : `${g} min`);

export default function AdminColaInformes() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [gapInput, setGapInput] = useState("");
  const [tick, setTick] = useState(0);
  const offsetRef = useRef(0); // diferencia reloj servidor − navegador
  const editingGap = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(BASE);
      setData(d);
      setError("");
      if (d.now) offsetRef.current = Date.parse(d.now) - Date.now();
      if (!editingGap.current) setGapInput(String(d.gap_minutes));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo leer la cola.");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    const c = setInterval(() => setTick((n) => n + 1), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, [load]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(res?.data) : okMsg);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo completar la acción");
    } finally {
      setBusy("");
    }
  };

  const toggle = () => {
    if (!data) return;
    if (data.paused) return act("toggle", () => api.post(`${BASE}/resume`), "Cola ENCENDIDA");
    if (!window.confirm("¿Apagar la cola? El informe que se esté generando se corta y vuelve a la cola.")) return;
    return act("toggle", () => api.post(`${BASE}/pause`), "Cola APAGADA");
  };

  const saveGap = (value) => {
    const n = Number(value);
    if (Number.isNaN(n) || n < 0 || n > 60) { toast.error("Escribe un número entre 0 y 60 minutos."); return; }
    editingGap.current = false;
    return act("gap", () => api.put(`${BASE}/settings`, { gap_minutes: n }), `Separación: ${fmtGap(n)}`);
  };

  const clearAll = () => {
    if (!window.confirm("¿Vaciar la cola? Todo lo pendiente y lo que se está generando queda DETENIDO y la cola se apaga.")) return;
    return act("clear", () => api.post(`${BASE}/clear`), (d) => `Cola vaciada (${d?.stopped ?? 0} informes detenidos)`);
  };

  const removeStudent = (g) => {
    if (!window.confirm(`¿Quitar a ${g.student_name} de la cola? Sus informes pendientes quedan detenidos.`)) return;
    return act(`st-${g.student_id}`, () => api.post(`${BASE}/student/${g.student_id}/remove`), (d) => `${g.student_name} quitado (${d?.removed ?? 0} informes)`);
  };

  const removeReport = (it, g) => {
    if (!window.confirm(`¿Quitar el informe ${TYPE_LABEL[it.type] || it.type} de ${g.student_name}?`)) return;
    return act(`rp-${it.id}`, () => api.post(`${BASE}/report/${it.id}/remove`), "Informe quitado de la cola");
  };

  const lockLeft = useMemo(() => {
    if (!data?.run_lock_until) return 0;
    const now = Date.now() + offsetRef.current;
    return Math.max(0, (Date.parse(data.run_lock_until) - now) / 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tick]);

  if (!data) {
    return (
      <div className="p-10 text-center text-[#6E7591]" data-testid="cola-loading">
        {error ? <span className="text-[#A52258]" data-testid="cola-error">{error}</span> : <><Loader2 size={18} className="inline animate-spin mr-2" /> Cargando cola…</>}
      </div>
    );
  }

  const on = !data.paused;
  const gap = Number(data.gap_minutes);

  return (
    <div className="space-y-6" data-testid="admin-cola-informes">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#EF7015]">Tecnología</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] inline-flex items-center gap-3">
            <ListChecks size={30} className="text-[#EF7015]" /> Cola de informes
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">Controla cuándo se generan los informes de diagnóstico (PDF). Se genera un informe a la vez.</p>
        </div>
        <button onClick={load} className="jasq-btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="cola-refresh">
          <RefreshCw size={12} /> Actualizar
        </button>
      </div>

      {/* Estado + encender/apagar */}
      <div className={`jasq-card p-5 border-2 ${on ? "border-[#3FA84D]/60" : "border-[#D64545]/50"}`} data-testid="cola-estado">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className={`w-12 h-12 rounded-2xl grid place-items-center ${on ? "bg-[#E7F4E9] text-[#2A7A36]" : "bg-[#FBE4E4] text-[#B03030]"}`}>
              <Power size={22} />
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">Estado</p>
              <p className={`font-display text-2xl font-extrabold ${on ? "text-[#2A7A36]" : "text-[#B03030]"}`} data-testid="cola-estado-texto">
                {on ? "ENCENDIDA" : "APAGADA"}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={toggle} disabled={busy === "toggle"} data-testid="cola-toggle"
              className={`${on ? "jasq-btn-ghost" : "jasq-btn-primary"} inline-flex items-center gap-2`}>
              {busy === "toggle" ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} />}
              {on ? "Apagar cola" : "Encender cola"}
            </button>
            <button onClick={clearAll} disabled={busy === "clear"} data-testid="cola-vaciar"
              className="jasq-btn-ghost text-[#B03030] inline-flex items-center gap-2">
              <Trash2 size={14} /> Vaciar cola
            </button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4 text-center">
          <Stat label="En cola" value={data.queued} testid="cola-n-queued" />
          <Stat label="Generando" value={data.generating} testid="cola-n-generating" />
          <Stat label="Próximo inicio" value={lockLeft > 0 ? fmtClock(lockLeft) : (on && data.queued ? "ya" : "—")} testid="cola-next" />
        </div>
        {!on && <p className="text-xs text-[#6E7591] mt-3">Con la cola apagada no se genera nada; lo que ya está en la lista espera hasta que la enciendas.</p>}
      </div>

      {/* Separación */}
      <div className="jasq-card p-5" data-testid="cola-gap-card">
        <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790] inline-flex items-center gap-1"><Timer size={12} /> Tiempo mínimo entre informes</p>
        <p className="font-display text-xl font-extrabold text-[#1F2547] mt-1" data-testid="cola-gap-actual">{fmtGap(gap)}</p>
        <p className="text-xs text-[#6E7591] mt-1">Es el tiempo que debe pasar desde que arranca un informe hasta que puede arrancar el siguiente. Menos minutos = informes más rápidos. 0 = solo se espera a que termine el anterior.</p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button className="jasq-btn-ghost px-2" aria-label="Reducir un minuto" data-testid="cola-gap-menos"
            disabled={busy === "gap" || gap <= 0} onClick={() => saveGap(Math.max(0, Math.round((gap - 1) * 10) / 10))}><Minus size={14} /></button>
          <input type="number" min="0" max="60" step="0.5" value={gapInput} data-testid="cola-gap-input"
            onFocus={() => { editingGap.current = true; }}
            onChange={(e) => setGapInput(e.target.value)}
            className="w-24 rounded-xl border-2 border-[#E1E5F0] px-3 py-2 text-sm font-bold text-[#1F2547]" />
          <button className="jasq-btn-ghost px-2" aria-label="Aumentar un minuto" data-testid="cola-gap-mas"
            disabled={busy === "gap" || gap >= 60} onClick={() => saveGap(Math.min(60, Math.round((gap + 1) * 10) / 10))}><Plus size={14} /></button>
          <button className="jasq-btn-primary text-sm" data-testid="cola-gap-guardar" disabled={busy === "gap"} onClick={() => saveGap(gapInput)}>Guardar</button>
          <span className="text-xs text-[#6E7591] ml-2">Atajos:</span>
          {PRESETS.map((p) => (
            <button key={p} onClick={() => saveGap(p)} disabled={busy === "gap"} data-testid={`cola-gap-preset-${p}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 ${gap === p ? "border-[#A43790] bg-[#A43790] text-white" : "border-[#E1E5F0] text-[#4C5478]"}`}>
              {p === 0 ? "0" : `${p} min`}
            </button>
          ))}
        </div>
      </div>

      {/* Cola por estudiante */}
      <div className="space-y-3" data-testid="cola-lista">
        <h2 className="font-display text-xl font-extrabold text-[#1F2547]">Cola por estudiante</h2>
        {data.students.length === 0 && (
          <div className="jasq-card p-8 text-center text-sm text-[#6E7591]" data-testid="cola-vacia">No hay informes en cola ni generándose.</div>
        )}
        {data.students.map((g) => (
          <div key={g.student_id} className="jasq-card p-4" data-testid={`cola-estudiante-${g.student_id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="font-extrabold text-[#1F2547]">{g.student_name} <span className="text-xs font-bold text-[#6E7591]">· {g.items.length} informe{g.items.length === 1 ? "" : "s"}</span></p>
              <button onClick={() => removeStudent(g)} disabled={busy === `st-${g.student_id}`} data-testid={`cola-quitar-estudiante-${g.student_id}`}
                className="text-xs font-extrabold text-[#B03030] inline-flex items-center gap-1 hover:underline">
                <UserX size={13} /> Quitar estudiante de la cola
              </button>
            </div>
            <ul className="mt-2 divide-y divide-[#E1E5F0]">
              {g.items.map((it) => (
                <li key={it.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`cola-item-${it.id}`}>
                  <span className="w-7 h-7 rounded-full bg-[#F3DDEE] text-[#A43790] text-xs font-extrabold grid place-items-center shrink-0">{it.position}</span>
                  <span className="flex-1 font-bold text-[#1F2547]">{TYPE_LABEL[it.type] || it.type}</span>
                  <span className={`text-[11px] font-extrabold rounded-full px-2 py-0.5 ${it.status === "generating" ? "bg-[#E8EEFB] text-[#2C49A0]" : "bg-[#FFF7DA] text-[#9C7A18]"}`}>
                    {it.status === "generating" ? "Generando…" : "En espera"}
                  </span>
                  <button onClick={() => removeReport(it, g)} disabled={busy === `rp-${it.id}`} aria-label="Quitar este informe" title="Quitar este informe"
                    data-testid={`cola-quitar-informe-${it.id}`} className="text-[#6E7591] hover:text-[#B03030]"><X size={15} /></button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {data.recent_failed?.length > 0 && (
        <details className="jasq-card p-4" data-testid="cola-fallidos">
          <summary className="cursor-pointer text-sm font-extrabold text-[#1F2547]">Detenidos o con error recientes ({data.recent_failed.length})</summary>
          <ul className="mt-2 divide-y divide-[#E1E5F0] text-xs">
            {data.recent_failed.map((f) => (
              <li key={f.id} className="py-1.5 flex gap-2">
                <b className="text-[#1F2547] w-32 shrink-0">{TYPE_LABEL[f.type] || f.type}</b>
                <span className="text-[#6E7591] break-words">{f.error || "—"}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-[#F7F8FA] py-2.5">
      <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591]">{label}</p>
      <p className="font-display text-xl font-extrabold text-[#1F2547]" data-testid={testid}>{value}</p>
    </div>
  );
}
